import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { DEFAULT_STATUSES } from 'src/app/request-v2/constants/request-status.constants';
import { Request, RequestLink, RequestLinkType, StatusType } from 'src/app/request-v2/models/request.model';
import { RequestsV2ImplService } from 'src/app/request-v2/services/requests-v2.impl.service';

@Component({
    selector: 'app-create-request-links',
    templateUrl: './create-request-links.component.html',
    styleUrls: ['../../../request-info-dialog/request-details/request-links/request-links.component.scss'],
})
export class CreateRequestLinksComponent implements OnInit, OnDestroy {
    @Input() links: RequestLink[] = [];

    @Output() linksChanged = new EventEmitter<RequestLink[]>();

    isLinking = false;
    searchTerm = '';
    searchSubject = new Subject<string>();
    searchResults: Request[] = [];
    isSearching = false;

    linkTypes = [
        { value: RequestLinkType.BLOCKS, label: 'request_v2.links.types.BLOCKS' },
        { value: RequestLinkType.IS_BLOCKED_BY, label: 'request_v2.links.types.IS_BLOCKED_BY' },
        { value: RequestLinkType.IS_DUPPLICATE_OF, label: 'request_v2.links.types.IS_DUPPLICATE_OF' },
        { value: RequestLinkType.IS_LINKED_TO, label: 'request_v2.links.types.IS_LINKED_TO' },
    ];

    newLinkType: RequestLinkType = RequestLinkType.BLOCKS;
    linkToDelete: RequestLink | null = null;
    openDropdownId: string | null = null;

    private searchSubscription: Subscription;

    constructor(
        private requestService: RequestsV2ImplService,
        private eRef: ElementRef,
        private translateService: TranslateService,
    ) {}

    @HostListener('document:click', ['$event'])
    clickout(event: any): void {
        if (!this.eRef.nativeElement.contains(event.target)) {
            this.openDropdownId = null;
            this.searchResults = [];
            return;
        }

        const clickedInsideDropdown = event.target.closest('.custom-dropdown');
        if (!clickedInsideDropdown) {
            this.openDropdownId = null;
        }

        const clickedInsideSearch = event.target.closest('.search-input-wrapper');
        if (!clickedInsideSearch) {
            this.searchResults = [];
        }
    }

    ngOnInit(): void {
        this.searchSubscription = this.searchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((term) => {
                    if (!term || term.length < 2) {
                        return [];
                    }
                    this.isSearching = true;
                    return this.requestService.getRequests({ search: term } as any, 1, 10);
                }),
            )
            .subscribe(
                (results: any) => {
                    this.isSearching = false;
                    if (results && results.docs) {
                        const linkedRequestIds = this.links.map((link) => this.getLinkedRequestId(link));
                        this.searchResults = results.docs.filter((request: Request) => !linkedRequestIds.includes(request._id));
                    } else {
                        this.searchResults = [];
                    }
                },
                (err) => {
                    this.isSearching = false;
                    console.error(err);
                },
            );
    }

    ngOnDestroy(): void {
        this.searchSubscription?.unsubscribe();
    }

    onSearch(term: string): void {
        this.searchTerm = term;
        if (term.length >= 2) {
            this.searchSubject.next(term);
        } else {
            this.searchResults = [];
            this.isSearching = false;
        }
    }

    clearSearch(): void {
        this.searchTerm = '';
        this.searchResults = [];
        this.isSearching = false;
    }

    startLinking(): void {
        this.clearSearch();
        this.isLinking = true;
        this.newLinkType = RequestLinkType.BLOCKS;
    }

    cancelLinking(): void {
        this.isLinking = false;
        this.clearSearch();
        this.searchSubject.next(this.searchTerm);
    }

    selectRequestToLink(targetRequest: Request): void {
        const newLink: RequestLink = {
            type: this.newLinkType,
            linkedRequest: targetRequest,
            linkedRequestId: targetRequest._id,
        };

        this.links = [...this.links.filter((link) => this.getLinkedRequestId(link) !== targetRequest._id), newLink];
        this.linksChanged.emit(this.links);
        this.cancelLinking();
    }

    toggleDropdown(linkId: string | undefined): void {
        if (!linkId) return;
        this.openDropdownId = this.openDropdownId === linkId ? null : linkId;
    }

    onLinkTypeChange(link: RequestLink, newType: RequestLinkType): void {
        if (link.type === newType) return;

        const linkedRequestId = this.getLinkedRequestId(link);
        this.openDropdownId = null;
        this.links = this.links.map((currentLink) => (this.getLinkedRequestId(currentLink) === linkedRequestId ? { ...currentLink, type: newType } : currentLink));
        this.linksChanged.emit(this.links);
    }

    confirmDelete(link: RequestLink): void {
        this.linkToDelete = link;
    }

    cancelDelete(): void {
        this.linkToDelete = null;
    }

    deleteLink(): void {
        if (!this.linkToDelete) return;

        const linkedRequestId = this.getLinkedRequestId(this.linkToDelete);
        this.links = this.links.filter((link) => this.getLinkedRequestId(link) !== linkedRequestId);
        this.linksChanged.emit(this.links);
        this.linkToDelete = null;
    }

    getStatus(req: any): StatusType | undefined {
        if (!req || !req.category || !req.category.statuses) return undefined;
        return req.category.statuses.find((status: any) => status._id === req.state || status.id === req.state);
    }

    getStatusLabel(status: StatusType | undefined): string {
        if (!status) return '';
        if (status.isDefault) {
            const defaultStatus = DEFAULT_STATUSES.find((item) => item.nameEn === status.name || item.nameFr === status.name);
            if (defaultStatus) {
                return this.translateService.currentLang === 'fr' ? defaultStatus.nameFr : defaultStatus.nameEn;
            }
        }
        return status.name;
    }

    getLabelForType(type: RequestLinkType): string {
        const found = this.linkTypes.find((item) => item.value === type);
        return found ? found.label : type;
    }

    getLinkedRequestId(link: RequestLink): string | undefined {
        return link.linkedRequestId || link.linkedRequest?._id;
    }

    getRequestCategoryLabel(request: Request): string {
        return request?.subCategory?.name || request?.category?.subCategories?.[0]?.name || request?.category?.name || '';
    }
}
